import React from 'react';
import Reveal from './Reveal';

const About = () => {
  const values = [
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
      title: 'Innovation First',
      desc: 'Pioneering AI solutions that drive real business value and competitive edge.',
      color: 'var(--color-blue)',
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      title: 'Quality Execution',
      desc: 'Delivering production-ready, battle-tested solutions with uncompromising excellence.',
      color: 'var(--color-green)',
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0" /></svg>,
      title: 'Client Partnership',
      desc: 'Building long-term relationships through trust, transparency, and measurable results.',
      color: 'var(--color-purple)',
    },
    {
      icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>,
      title: 'Product-Led',
      desc: 'We build our own AI products, so we understand your challenges from the inside out.',
      color: 'var(--color-gold)',
    },
  ];

  const highlights = [
    'AI-Driven Product Development', 'Custom Software Solutions',
    'Digital Transformation', 'Full-Stack Engineering',
    'SaaS Platforms', 'Mobile Applications',
  ];

  return (
    <section id="about" className="section-padding bg-brand-surface relative overflow-hidden scroll-mt-16">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none glow-blob"
        style={{ background: 'rgb(var(--color-purple))' }} />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none glow-blob"
        style={{ background: 'rgb(var(--color-blue))' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <Reveal from="up" className="section-header">
          <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-sm font-medium text-brand-cyan border border-brand-cyan/20 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan" />
            Who We Are
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-brand-text mb-5 leading-tight">
            About <span className="text-gradient">Avarnix</span>
          </h2>
          <p className="text-lg text-brand-muted max-w-2xl mx-auto">
            A product-based AI & software company headquartered in Bangalore, India — building the next generation of intelligent digital solutions.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal from="left">
            <div className="space-y-6">
              <p className="text-brand-muted text-lg leading-relaxed">
                Avarnix Technologies was born from a simple belief: <span className="text-brand-text font-semibold">great AI should be accessible to every business.</span> We specialize in AI-driven products like <span className="text-brand-blue font-semibold">FitPulseBot</span> — a comprehensive health SaaS ecosystem — while simultaneously delivering custom AI and software solutions across industries.
              </p>
              <p className="text-brand-muted text-lg leading-relaxed">
                Our approach is unique: we build, test, and scale our own products first. This gives us real-world insight into what works, what doesn't, and how to engineer solutions that truly matter to your business.
              </p>
              <div className="grid grid-cols-2 gap-3 pt-2">
                {highlights.map((h, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-brand-blue flex-shrink-0" />
                    <span className="text-brand-text text-sm font-medium">{h}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl p-5 border-l-4 border-brand-blue" style={{ background: 'var(--card-bg)' }}>
                <p className="text-brand-text font-semibold text-xl italic">"Where Ideas Meet AI."</p>
                <p className="text-brand-muted text-sm mt-2">Transforming innovative ideas into AI-powered realities, every single day.</p>
              </div>
            </div>
          </Reveal>

          <Reveal from="right">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((v, i) => (
                <div key={i} className="card group hover:shadow-card-hover transition-all duration-300">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ background: `rgba(${v.color}, 0.12)`, color: `rgb(${v.color})` }}>
                    {v.icon}
                  </div>
                  <h4 className="text-brand-text font-bold mb-2">{v.title}</h4>
                  <p className="text-brand-muted text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default About;
